import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Users, Mail, Send, CheckCircle, XCircle, Unlink, Clock, Shield, ArrowRightLeft } from 'lucide-react';
import { useSyncRefresh, notifySyncUpdate } from '../hooks/useSyncRefresh';

import API from '../utils/api';

function CoParentLink() {
  var [link, setLink] = useState(null);
  var [pending, setPending] = useState([]);
  var [loading, setLoading] = useState(true);
  var [inviteEmail, setInviteEmail] = useState('');
  var [sending, setSending] = useState(false);
  var [actionLoading, setActionLoading] = useState('');

  function loadData() {
    Promise.all([
      axios.get(API + '/coparent/link', { withCredentials: true }),
      axios.get(API + '/coparent/pending', { withCredentials: true }),
    ]).then(function (res) {
      setLink(res[0].data.link);
      setPending(res[1].data);
    }).catch(function () {}).finally(function () { setLoading(false); });
  }

  useEffect(function () { loadData(); }, []);
  useSyncRefresh(useCallback(function() { loadData(); }, []));

  function handleInvite(e) {
    e.preventDefault();
    if (!inviteEmail.trim() || sending) return;
    setSending(true);
    axios.post(API + '/coparent/invite', { email: inviteEmail }, { withCredentials: true })
      .then(function () { setInviteEmail(''); loadData(); })
      .catch(function (err) { alert(err.response ? err.response.data.detail : 'Failed to send invite'); })
      .finally(function () { setSending(false); });
  }

  function handleRespond(id, action) {
    setActionLoading(id);
    axios.patch(API + '/coparent/respond/' + id, { action: action }, { withCredentials: true })
      .then(function () { loadData(); })
      .catch(function (err) { alert(err.response ? err.response.data.detail : 'Failed'); })
      .finally(function () { setActionLoading(''); });
  }

  function handleUnlink() {
    if (!window.confirm('Unlink your co-parent? This will remove the shared calendar view.')) return;
    setActionLoading('unlink');
    axios.delete(API + '/coparent/unlink', { withCredentials: true })
      .then(function () { setLink(null); loadData(); })
      .catch(function (err) {
        alert(err.response ? err.response.data.detail : 'Failed to unlink co-parent');
      })
      .finally(function () { setActionLoading(''); });
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="coparent-loading">
        <div className="w-8 h-8 border-2 border-[#2D2560] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="coparent-page">
      <h2 className="text-lg font-semibold flex items-center gap-2">
        <Users className="w-5 h-5 text-[#2D2560]" />
        Co-Parent Link
      </h2>

      {/* Active link */}
      {link && link.status === 'active' && (
        <div className="card-surface p-4 sm:p-6" data-testid="active-link">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3 sm:gap-4 min-w-0">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#10B981]/20 flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-[#10B981]" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold flex flex-wrap items-center gap-2">
                  <span>Linked with</span>
                  <span className="text-[#2D2560] break-all">{link.is_initiator ? link.user_b_name : link.user_a_name}</span>
                  <span className="px-2 py-0.5 bg-[#10B981]/20 text-[#10B981] text-xs rounded-full font-bold">ACTIVE</span>
                </p>
                <p className="text-xs text-white/40 break-all">{link.is_initiator ? link.user_b_email : link.user_a_email}</p>
              </div>
            </div>
            <button onClick={handleUnlink}
              className="self-stretch sm:self-auto px-3 py-2 bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/30 rounded-lg text-xs font-medium hover:bg-[#EF4444]/20 transition-colors flex items-center justify-center gap-1.5 disabled:opacity-40 flex-shrink-0"
              disabled={actionLoading === 'unlink'}
              data-testid="unlink-btn">
              <Unlink className="w-3.5 h-3.5" /> {actionLoading === 'unlink' ? 'Unlinking...' : 'Unlink'}
            </button>
          </div>
        </div>
      )}

      {/* Pending link (sent by me) */}
      {link && link.status === 'pending' && link.is_initiator && (
        <div className="card-surface p-4 sm:p-6" data-testid="pending-sent">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#EAB308]/20 flex items-center justify-center flex-shrink-0">
              <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-[#EAB308]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold break-words">Invite sent to <span className="text-[#EAB308] break-all">{link.user_b_email}</span></p>
              <p className="text-xs text-white/40">Waiting for them to accept...</p>
            </div>
            <button onClick={handleUnlink}
              className="self-stretch sm:self-auto px-3 py-2 bg-white/5 text-white/40 border border-white/10 rounded-lg text-xs hover:bg-white/10 disabled:opacity-40 flex-shrink-0"
              disabled={actionLoading === 'unlink'}
              data-testid="cancel-invite-btn">{actionLoading === 'unlink' ? 'Canceling...' : 'Cancel'}</button>
          </div>
        </div>
      )}

      {/* Incoming invites */}
      {pending.length > 0 && (
        <div className="space-y-3" data-testid="pending-invites">
          <p className="text-sm font-semibold text-white/60">Incoming Invitations</p>
          {pending.map(function (p) {
            return (
              <div key={p.id} className="card-surface p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid={'invite-' + p.id}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-[#2D2560]/20 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#2D2560]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium break-words"><span className="text-[#2D2560]">{p.user_a_name}</span> wants to link as your co-parent</p>
                    <p className="text-xs text-white/40 break-all">{p.user_a_email}</p>
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button onClick={function () { handleRespond(p.id, 'accept'); }} disabled={actionLoading === p.id}
                    className="flex-1 sm:flex-none px-3 py-1.5 bg-[#10B981]/20 text-[#10B981] border border-[#10B981]/30 rounded-lg text-xs font-medium hover:bg-[#10B981]/30 disabled:opacity-40 flex items-center justify-center"
                    data-testid={'accept-' + p.id}>
                    <CheckCircle className="w-3.5 h-3.5 inline mr-1" /> Accept
                  </button>
                  <button onClick={function () { handleRespond(p.id, 'decline'); }} disabled={actionLoading === p.id}
                    className="flex-1 sm:flex-none px-3 py-1.5 bg-[#EF4444]/20 text-[#EF4444] border border-[#EF4444]/30 rounded-lg text-xs font-medium hover:bg-[#EF4444]/30 disabled:opacity-40 flex items-center justify-center"
                    data-testid={'decline-' + p.id}>
                    <XCircle className="w-3.5 h-3.5 inline mr-1" /> Decline
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Invite form (only if no active/pending link) */}
      {!link && (
        <form onSubmit={handleInvite} className="card-surface p-4 sm:p-6" data-testid="invite-form">
          <p className="text-sm font-semibold mb-1">Invite your co-parent</p>
          <p className="text-xs text-white/40 mb-4">Link your accounts to share calendar views and coordinate schedules</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative min-w-0">
              <Mail className="w-4 h-4 text-white/30 absolute left-3 top-1/2 -translate-y-1/2" />
              <input type="email" required value={inviteEmail}
                onChange={function (e) { setInviteEmail(e.target.value); }}
                className="w-full bg-white/5 border border-white/10 text-white text-sm rounded-lg pl-10 pr-3 py-2.5"
                placeholder="coparent@email.com"
                data-testid="invite-email-input" />
            </div>
            <button type="submit" disabled={sending}
              className="px-5 py-2.5 bg-[#2D2560] text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-40 transition-opacity flex items-center justify-center gap-2 flex-shrink-0"
              data-testid="send-invite-btn">
              <Send className="w-4 h-4" /> {sending ? 'Sending...' : 'Send Invite'}
            </button>
          </div>
        </form>
      )}

      {/* No link state */}
      {!link && pending.length === 0 && (
        <div className="card-surface p-8 text-center">
          <Users className="w-12 h-12 text-white/10 mx-auto mb-3" />
          <p className="text-white/40 text-sm mb-1">No co-parent linked yet</p>
          <p className="text-white/25 text-xs">Invite your co-parent above to share calendars and coordinate schedules</p>
        </div>
      )}
    </div>
  );
}

export default CoParentLink;
